import React, { useEffect, useRef } from 'react';
import * as dc from 'dc';
import * as d3 from 'd3';
import $ from 'jquery';
import D3ChartService from './d3ChartService';

interface BarChartProps {
  reportData: {
    series: any[];
    categories: string[];
  };
  toolTipLabelFunction?: (stackName: string, value: any, altValue: any) => string;
  height: number;
  minWidth?: number;
  bottomMargin?: number;
}

const BarChart: React.FC<BarChartProps> = ({
  reportData,
  toolTipLabelFunction,
  height,
  minWidth,
  bottomMargin,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const {
    buildChartData,
    watchResizeEvents,
    resizeChart,
    adjustBar,
    adjustBarLabels,
    adjustLegend,
    addTooltips,
  } = D3ChartService;

  useEffect(() => {
    if (reportData && chartRef.current) {
      const chartData = buildChartData(reportData.series, reportData.categories);
      const barChart = dc.barChart(chartRef.current as any);

      if (minWidth) {
        $(chartRef.current).css('min-width', `${minWidth}px`);
      }

      if (bottomMargin) {
        barChart.margins().bottom = bottomMargin;
      }
      barChart.margins().top = 30;
      barChart.margins().left = 50;

      barChart
        .height(height)
        .dimension(chartData.dimension)
        .group(chartData.group, chartData.stackNames[0])
        .valueAccessor(d => d.value.values[0])
        .x(d3.scale.ordinal().domain(reportData.categories))
        .xUnits(dc.units.ordinal)
        .ordering(d => reportData.categories.indexOf(d.key))
        .y(d3.scale.linear().domain([0, 1.1 * chartData.maxValue]))
        .renderHorizontalGridLines(true)
        .elasticX(true)
        .centerBar(true)
        .gap(10);

      barChart.xAxis().tickSize(2);
      barChart
        .yAxis()
        .tickSize(2)
        .ticks(5);
      barChart.legend(
        dc
          .legend()
          .x(5)
          .y(barChart.height() - (chartData.stackSize*22.5)+40),
      );

      // stacked data - the first one is from the "valueAccessor" above
      for (let i = 1; i < chartData.stackSize; i++) {
        barChart.stack(
          chartData.group,
          chartData.stackNames[i],
          d => d.value.values[i],
        );
      }

      // tooltips
      const toolTipLabel = toolTipLabelFunction || ((stackName, value, altValue) => `${stackName}: ${altValue}`);
      for (let i = 0; i < chartData.stackSize; i++) {
        barChart.title(chartData.stackNames[i], d =>
          toolTipLabel(
            chartData.stackNames[i],
            d.value.values[i],
            d.value.altValues[i],
          ),
        );
      }

      const resizeWatch = watchResizeEvents(barChart, chartRef.current, resizeChart);

      // Set transition duration to 0 before rendering
      barChart.transitionDuration(0);

      barChart.on('renderlet', adjustBar);
      barChart.on('renderlet', adjustBarLabels);
      barChart.on('renderlet', adjustLegend);
      barChart.on('renderlet', addTooltips);

      barChart.render();

      // Restore transition duration to 750 after rendering
      barChart.transitionDuration(750);

      return () => {
        if (resizeWatch) {
          resizeWatch();
        }
      };
    }
  }, [reportData, toolTipLabelFunction, height, minWidth, bottomMargin, buildChartData, watchResizeEvents, resizeChart, adjustBar, adjustBarLabels, adjustLegend, addTooltips]);

  return <div ref={chartRef} id="bar-chart"></div>;
};

export default BarChart;